/** Read-only display primitives shared across detail views (tours, bookings,
 *  orders, …). Field groups with labelled values, boolean/url/link/chip
 *  variants. Editing forms (RHF + zod) live per-feature and don't use these. */
import { Box, Card, CardContent, Link, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';

export function SectionCard({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            {title}
          </Typography>
          {action}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

/** A labelled value. `actions` wraps the value in a hover/click popover
 *  (open / filter / copy — see `ValueActions`); empty values never get one. */
export function Field({
  label,
  value,
  actions,
}: {
  label: string;
  value: ReactNode;
  actions?: ValueAction[];
}) {
  const empty = value === null || value === undefined || value === '';
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: empty ? 'text.disabled' : 'text.primary' }}
      >
        {empty ? '—' : actions?.length ? <ValueActions actions={actions}>{value}</ValueActions> : value}
      </Typography>
    </Box>
  );
}

/** Two-column responsive field layout. */
export function FieldGrid({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        columnGap: 3,
      }}
    >
      {children}
    </Box>
  );
}

export function BoolField({ label, value }: { label: string; value: boolean | null | undefined }) {
  return <Field label={label} value={value === null || value === undefined ? '—' : value ? 'Yes' : 'No'} />;
}

/** Labelled external link (new tab). `actions` adds e.g. a copy of the URL. */
export function UrlField({
  label,
  url,
  actions,
}: {
  label: string;
  url: string | null | undefined;
  actions?: ValueAction[];
}) {
  return (
    <Field
      label={label}
      value={url ? (
        <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
          {url}
        </Link>
      ) : '—'}
      {...(actions && url && { actions })}
    />
  );
}

/** Labelled internal (react-router) link. `to` null/empty renders an em dash.
 *  The inline link stays same-tab (⌘-click for a new tab); `actions` adds the
 *  extra filter/copy items in the popover. */
export function LinkField({
  label,
  to,
  text,
  actions,
}: {
  label: string;
  to: string | null | undefined;
  text: ReactNode;
  actions?: ValueAction[];
}) {
  const empty = text === null || text === undefined || text === '';
  return (
    <Field
      label={label}
      value={to && !empty ? (
        <Link component={RouterLink} to={to} underline="hover">
          {text}
        </Link>
      ) : (empty ? '—' : text)}
      {...(actions && !empty && { actions })}
    />
  );
}

export function ChipList({ items }: { items: { id: number; name: string | null }[] }) {
  if (!items.length) return <span>—</span>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {items.map((i) => (
        <Typography key={i.id} variant="body2" component="span"
          sx={{ px: 1, py: 0.25, bgcolor: 'action.hover', borderRadius: 1 }}>
          {i.name ?? `#${i.id}`}
        </Typography>
      ))}
    </Stack>
  );
}
